/** Inline loading indicator, ported from the `.spin` rule of the old pages. */

import { useLingui } from '@lingui/react/macro';
import type { ReactNode } from 'react';

export interface SpinnerProps {
  /** `on-accent` renders the white variant used inside filled buttons. */
  variant?: 'default' | 'on-accent';
  /** `lg` is the page-level spinner. */
  size?: 'sm' | 'lg';
  /** Accessible label; defaults to 'Lädt…' in the UI language. */
  label?: string;
}

export function Spinner({ variant = 'default', size = 'sm', label }: SpinnerProps): ReactNode {
  const { t } = useLingui();
  const classes = ['spin'];
  if (variant === 'on-accent') classes.push('on-accent');
  if (size === 'lg') classes.push('lg');
  return <span className={classes.join(' ')} role="status" aria-label={label ?? t`Lädt…`} />;
}

export interface PageSpinnerProps {
  /** Text shown below the spinner; defaults to 'Lädt…' in the UI language. */
  text?: string;
}

/** Full-height loading state for route transitions and initial loads. */
export function PageSpinner({ text }: PageSpinnerProps): ReactNode {
  const { t } = useLingui();
  const shown = text ?? t`Lädt…`;
  return (
    <div className="loading-page">
      <Spinner size="lg" label={shown} />
      <span>{shown}</span>
    </div>
  );
}

export default Spinner;
